import { createContext, useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import {
  applyInstituteBranding,
  getInstituteStampPathFromRecord,
  NO_IMAGE_SRC,
  resolveInstituteLogoSrc,
  resolveInstituteStampSrc,
} from "../../utils/instituteBranding";
import {
  clearInstituteBrandingCookies,
  hasValidInstituteIdCookie,
  setDefaultInvalidInstituteIdCookie,
} from "../../utils/instituteIdCookie";
import ApiEndpoints from "./ApiEndpoints";

const InstituteContext = createContext();

export const InstituteProvider = ({ children }) => {
    const location = useLocation(); 
    const navigate = useNavigate();
    const { logout } = useAuth();
  
    const [institute, setInstitute] = useState({
      name: "",
      id: "",
      logo: NO_IMAGE_SRC,
      type: "",
      loading: true
    });

    const setInstituteId = (nextInstituteId) => {
      const normalizedId = String(nextInstituteId ?? "").trim();
      if (!normalizedId || Number(normalizedId) <= 0) return;

      Cookies.set("institute_id", normalizedId, { expires: 7, path: "/", sameSite: "Lax" });
      sessionStorage.setItem("institute_confirmed", "true");
      sessionStorage.setItem("institute_confirmed_timestamp", Date.now().toString());

      const cookieName = Cookies.get("name") || Cookies.get("institute_name") || "";
      const cookieLogo = Cookies.get("institute_logo") || "no_image.png";
      const cookieType = String(Cookies.get("institute_type") || "")
        .trim()
        .toLowerCase();

      setInstitute((prev) => ({
        ...prev,
        id: normalizedId,
        name: prev?.name || cookieName,
        logo: prev?.logo || resolveInstituteLogoSrc(cookieLogo),
        type: cookieType || prev?.type || "",
        loading: false,
      }));
    };
  
    useEffect(() => {
      const storedInstituteName = Cookies.get("name");
      const storedInstituteId = Cookies.get("institute_id");
      const storedInstituteLogo = Cookies.get("institute_logo");
      const instituteConfirmed = sessionStorage.getItem("institute_confirmed");
  
      const isAuthPage = location.pathname === "/LoginSignup";
      const isParentRegPage = location.pathname === "/Parent/Registration";
      const allowWithoutValidInstitute = isAuthPage || isParentRegPage;


      let isAdminUser = false;
      try {
        const raw = Cookies.get("user");
        if (raw) isAdminUser = JSON.parse(raw)?.user_type === "admin";
      } catch {
        /* ignore */
      }
  
      // ❌ stale cookie check — only for admin (non-admins never set institute_confirmed at login)
      const hasLoggedInUser = !!Cookies.get("user");

      if (hasValidInstituteIdCookie() && !instituteConfirmed && isAdminUser) {
        clearInstituteBrandingCookies();
        if (hasLoggedInUser) {
          setDefaultInvalidInstituteIdCookie();
        }
        applyInstituteBranding("", "no_image.png");
  
        if (!allowWithoutValidInstitute) {
          navigate("/LoginSignup", { replace: true });
        }
        setInstitute({
          name: "",
          id: "",
          logo: NO_IMAGE_SRC,
          type: "",
          loading: false,
        });
        return;
      }
  
      // ❌ missing / invalid / zero institute_id
      if (!hasValidInstituteIdCookie()) {
        applyInstituteBranding("", "no_image.png");

        // Non-admin: full logout (clears user, roles, institute cookies) — avoids LoginSignup ↔ portal loop
        if (!isAdminUser && hasLoggedInUser) {
          logout({ invalidInstitute: true });
          return;
        }

        if (!allowWithoutValidInstitute && hasLoggedInUser) {
          if (isAdminUser) {
            setDefaultInvalidInstituteIdCookie();
          } else {
            clearInstituteBrandingCookies();
          }
          navigate("/LoginSignup", { replace: true });
        } else if (isAdminUser && hasLoggedInUser) {
          setDefaultInvalidInstituteIdCookie();
        }

        setInstitute({
          name: "",
          id: "",
          logo: NO_IMAGE_SRC,
          type: "",
          loading: false,
        });
        return;
      }
  
      // ✅ set global data
      const logoVal = storedInstituteLogo || "no_image.png";
      const storedInstituteType = String(Cookies.get("institute_type") || "")
        .trim()
        .toLowerCase();
      setInstitute({
        name: storedInstituteName || "",
        id: storedInstituteId || "",
        logo: resolveInstituteLogoSrc(logoVal),
        type: storedInstituteType,
        loading: false
      });
      applyInstituteBranding(storedInstituteName || "", logoVal);
  
    }, [location.pathname, navigate, logout]);

    /**
     * Sync institute_type (and fill missing branding) from GET /users/institute.
     * Type sync always runs — branding cookies alone used to skip this and left coaching gates stale.
     */
    useEffect(() => {
      const isAuthPage = location.pathname === "/LoginSignup";
      const isParentRegPage = location.pathname === "/Parent/Registration";
      const isManageHolidaysPage = location.pathname === "/ManageHolidays";
      if (isAuthPage || isParentRegPage || isManageHolidaysPage) return;
      if (!hasValidInstituteIdCookie()) return;

      const instituteId = String(Cookies.get("institute_id") || "").trim();
      if (!instituteId || Number(instituteId) <= 0) return;

      const existingLogo = Cookies.get("institute_logo");
      const existingStamp = Cookies.get("stamp_url_img") || Cookies.get("institute_stamp");
      const existingName = Cookies.get("name") || Cookies.get("institute_name");

      const missingBranding =
        !existingLogo || existingLogo === "no_image.png" || !existingStamp || !existingName;

      let cancelled = false;
      (async () => {
        try {
          const res = await ApiEndpoints.getAllInstitutesAssinged(); // GET /users/institute
          const list = Array.isArray(res?.data?.data) ? res.data.data : [];
          const matched = list.find(
            (row) => String(row?.institute_id ?? row?.id ?? "") === instituteId,
          );
          if (!matched) return;

          const resolvedInstituteType = String(
            matched?.institute_type || matched?.type || "",
          )
            .trim()
            .toLowerCase();
          if (resolvedInstituteType) {
            Cookies.set("institute_type", resolvedInstituteType, { expires: 7 });
            try {
              const rawUser = Cookies.get("user");
              if (rawUser) {
                const userData = JSON.parse(rawUser);
                userData.institute_type = resolvedInstituteType;
                Cookies.set("user", JSON.stringify(userData));
              }
            } catch {
              /* ignore */
            }
          }

          if (missingBranding) {
            const nextName = matched?.name || matched?.institute_name || "";
            const nextLogo = matched?.institute_logo || "";
            const stampPath = getInstituteStampPathFromRecord(matched);

            if (nextName) {
              Cookies.set("name", nextName, { expires: 7 });
              Cookies.set("institute_name", nextName, { expires: 7 });
            }
            if (matched?.address) Cookies.set("address", matched.address, { expires: 7 });
            if (matched?.pincode != null) Cookies.set("pincode", String(matched.pincode), { expires: 7 });
            if (matched?.active_session?.session_name) {
              Cookies.set("session_name", matched.active_session.session_name, { expires: 7 });
            }
            if (matched?.active_session?.id != null) {
              Cookies.set("academic_session_id", String(matched.active_session.id), { expires: 7 });
            }

            if (nextLogo) {
              const resolvedLogo = resolveInstituteLogoSrc(nextLogo);
              Cookies.set("institute_logo", resolvedLogo, { expires: 7 });
            }

            if (stampPath) {
              const resolvedStamp = resolveInstituteStampSrc(stampPath);
              Cookies.set("stamp_url_img", resolvedStamp, { expires: 7 });
              Cookies.set("institute_stamp", resolvedStamp, { expires: 7 });
            }
          }

          if (cancelled) return;
          const finalName = Cookies.get("name") || "";
          const finalLogo = Cookies.get("institute_logo") || "no_image.png";
          const finalType = String(
            resolvedInstituteType || Cookies.get("institute_type") || "",
          )
            .trim()
            .toLowerCase();
          setInstitute((prev) => ({
            ...prev,
            name: finalName || prev?.name || "",
            id: instituteId,
            logo: resolveInstituteLogoSrc(finalLogo),
            type: finalType || prev?.type || "",
            loading: false,
          }));
          if (missingBranding) {
            applyInstituteBranding(finalName || "", finalLogo);
          }
        } catch {
          // ignore; portal will fall back to placeholder branding
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [location.pathname]);
  
    return (
      <InstituteContext.Provider value={{ ...institute, setInstituteId }}>
        {children}
      </InstituteContext.Provider>
    );
  };
// custom hook
export const useInstitute = () => useContext(InstituteContext);